"use client";

import { Button } from "@heroui/react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import React, { useEffect } from "react";
import AutoSuggest from "react-autosuggest";
import {
  AiOutlineCloseCircle,
  AiOutlineLoading3Quarters,
  AiOutlineMinus,
  AiOutlinePlus,
} from "react-icons/ai";
import { CiCalendar, CiLocationOn } from "react-icons/ci";
import { FlightTypeEnum } from "../../enum/flightType";
import { useFlightStore } from "../../store/flight-store";
import { useRouter } from "@/i18n/navigation";

interface FlightSearchFormProps {
  initialArrivalAirport?: string;
  noOverlap?: boolean;
}

const FlightSearchForm = ({ initialArrivalAirport, noOverlap }: FlightSearchFormProps) => {
  const t = useTranslations("form_flight");
  const { locale } = useParams();
  const router = useRouter();
  // Zustand store
  const {
    quoteData,
    flightType,
    flights,
    isSubmitting,
    airports,
    airportSuggestions,
    setFlightType,
    setFlights,
    addFlightLeg,
    updateFlightLeg,
    removeFlightLeg,
    setIsSubmitting,
    setAirports,
    setAirportSuggestions,
    setQuoteData,
    loadPersistedQuote,
    resetForm,
  } = useFlightStore();

  useEffect(() => {
    async function fetchAirports() {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_APP_URL}/api/v1/airport`
      );
      const data = await res.json();
      console.log(data?.data);
      setAirports(data?.data);
    }

    // Load persisted quote data on component mount
    loadPersistedQuote();
    fetchAirports();
  }, [loadPersistedQuote, setAirports]);

  useEffect(() => {
    if (initialArrivalAirport && airports.length > 0) {
      const match = airports.find(
        (a) =>
          a.iata && a.iata.toUpperCase() === initialArrivalAirport.toUpperCase()
      );
      // The destination page's airport_code is authoritative for this page, so
      // always sync the "To" field to it — overriding any value persisted in
      // localStorage from a previously visited destination. Without this, a
      // stale persisted airport (e.g. King Khaled Intl) sticks regardless of the
      // CMS airport_code for the current destination.
      if (match && flights[0]?.toAirport !== match.name) {
        updateFlightLeg(0, "toAirport", match.name);
      }
    }
  }, [initialArrivalAirport, airports]);

  const handleFlightTypeChange = (type: FlightTypeEnum) => {
    setFlightType(type);
  };

  const handleAddMulticityFlight = () => {
    addFlightLeg();
  };

  const handleChange = (
    index: number,
    field: string,
    value: string | number
  ) => {
    updateFlightLeg(index, field as any, value);
  };

  const decreasePerson = (index: number, field: string, value: number) => {
    if (value > 1) {
      updateFlightLeg(index, field as any, value - 1);
    }
  };

  const increasePerson = (index: number, field: string, value: number) => {
    updateFlightLeg(index, field as any, value + 1);
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    setIsSubmitting(true);
    event.preventDefault();
    try {
      setQuoteData({
        flights,
        flightType,
      });

      router.push(`/estimate-price`, { locale: locale as string });
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteRow = (index: number) => {
    removeFlightLeg(index);
  };

  const getAirports = (value: string) => {
    // Find the complete airport object from the suggestions based on the selected value (name)
    // console.log(airports);
    console.log(value);
    console.log(airports);
    const filteredAirports = airports
      .filter(
        (airport: { name: string; city: string; iata: string; icao: string }) =>
          airport.name.toLowerCase().includes(value.toLowerCase()) ||
          airport.city.toLowerCase().includes(value.toLowerCase()) ||
          airport.iata.toLowerCase().includes(value.toLowerCase()) ||
          airport.icao.toLowerCase().includes(value.toLowerCase())
      )
      .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
    console.log(filteredAirports);
    setAirportSuggestions(filteredAirports);
  };

  const renderFormFields = () => {
    return flights.map((flight, index) => (
      <React.Fragment key={index}>
        <div className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:flex xl:justify-between xl:gap-4">
          <AiOutlineCloseCircle
            className={`text-2xl absolute -top-9 left-1/2 transform -translate-x-1/2 cursor-pointer ${
              index === 0 ? "hidden" : ""
            }`}
            onClick={(e) => deleteRow(index)}
          />
          <div className="flex flex-col gap-2.5 xl:w-full">
            <label
              htmlFor="fromAirport"
              className="text-sm font-medium text-gray-700"
            >
              {t("source")}
            </label>
            <div className="w-full flex items-center gap-3 border border-gray-300 rounded-lg bg-white h-14 px-4">
              <CiLocationOn className="text-xl text-gray-500" />

              <AutoSuggest
                suggestions={airportSuggestions}
                onSuggestionsFetchRequested={({ value }) => getAirports(value)}
                onSuggestionsClearRequested={() => setAirportSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion.name}
                renderSuggestion={(suggestion) => (
                  <span>
                    {suggestion.name +
                      " " +
                      `(${suggestion.iata})` +
                      `(${suggestion.icao})`}
                  </span>
                )}
                inputProps={{
                  type: "text",
                  id: "fromAirport",
                  className:
                    "flex-1 border-0 outline-none w-full text-gray-800 text-base font-normal leading-6 placeholder:text-gray-600",
                  placeholder: t("searchAirport"),
                  value: flight.fromAirport,
                  onChange: (event, { newValue }) => {
                    handleChange(index, "fromAirport", newValue);
                  },
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2.5 xl:w-full">
            <label
              htmlFor="toAirport"
              className="text-sm font-medium text-gray-700"
            >
              {t("destination")}
            </label>
            <div className="w-full flex items-center gap-3 border border-gray-300 rounded-lg bg-white h-14 px-4">
              <CiLocationOn className="text-xl text-gray-500" />

              <AutoSuggest
                suggestions={airportSuggestions}
                onSuggestionsFetchRequested={({ value }) => getAirports(value)}
                onSuggestionsClearRequested={() => setAirportSuggestions([])}
                getSuggestionValue={(suggestion) => suggestion.name}
                renderSuggestion={(suggestion) => (
                  <span>
                    {suggestion.name +
                      " " +
                      `(${suggestion.iata})` +
                      `(${suggestion.icao})`}
                  </span>
                )}
                inputProps={{
                  id: "toAirport",
                  type: "text",
                  className:
                    "flex-1 border-0 outline-none w-full text-gray-800 text-base font-normal leading-6 placeholder:text-gray-600",
                  placeholder: t("searchAirport"),
                  value: flight.toAirport,
                  onChange: (event, { newValue }) => {
                    handleChange(index, "toAirport", newValue);
                  },
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2.5 xl:w-full">
            <label
              htmlFor="departureDate"
              className="text-sm font-medium text-gray-700"
            >
              {t("departure")}
            </label>
            <div className="w-full flex items-center gap-3 border border-gray-300 rounded-lg bg-white h-14 px-4 focus-within:ring-2 focus-within:ring-blue-500">
              <CiCalendar className="text-xl text-gray-500 shrink-0" />
              <input
                id="departureDate"
                className="flex-1 min-w-0 border-0 outline-none bg-transparent text-gray-800 text-base font-normal leading-6"
                type="datetime-local"
                value={flight.departureDate}
                onChange={(e) =>
                  handleChange(index, "departureDate", e.target.value)
                }
                required
              />
            </div>
          </div>
          {flightType === "ROUNDWAY" && (
            <div className="flex flex-col gap-2.5 xl:w-full">
              <label
                htmlFor="return"
                className="text-sm font-medium text-gray-700"
              >
                {t("return")}
              </label>
              <div className="w-full flex items-center gap-3 border border-gray-300 rounded-lg bg-white h-14 px-4 focus-within:ring-2 focus-within:ring-blue-500">
                <CiCalendar className="text-xl text-gray-500 shrink-0" />
                <input
                  id="return"
                  type="datetime-local"
                  className="flex-1 min-w-0 border-0 outline-none bg-transparent text-gray-800 text-base font-normal leading-6"
                  value={flight.returnDate}
                  onChange={(e) =>
                    handleChange(index, "returnDate", e.target.value)
                  }
                  required
                />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2.5 xl:w-full xl:flex-shrink-2">
            <label
              htmlFor="passengers"
              className="text-sm font-medium text-gray-700 capitalize"
            >
              {t("passengers")}
            </label>
            <div className="flex items-center gap-4 max-w-32 border border-gray-300 rounded-lg bg-white h-14 px-2">
              <button
                type="button"
                className="w-6 h-6 text-base font-medium flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-gray-100 rounded"
                onClick={(e) =>
                  decreasePerson(index, "passengers", flight.passengers)
                }
                aria-label="Decrease passengers"
              >
                <AiOutlineMinus />
              </button>
              <input
                id="passengers"
                type="number"
                min="1"
                className="text-center w-8 border-0 outline-none text-gray-800 text-base font-normal"
                value={flight.passengers}
                onChange={(e) => {
                  const next = parseInt(e.target.value);
                  handleChange(
                    index,
                    "passengers",
                    isNaN(next) || next < 1 ? 1 : next
                  );
                }}
                required
              />
              <button
                type="button"
                className="w-6 h-6 text-base font-medium flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-gray-100 rounded"
                onClick={(e) =>
                  increasePerson(index, "passengers", flight.passengers)
                }
                aria-label="Increase passengers"
              >
                <AiOutlinePlus />
              </button>
            </div>
          </div>
        </div>
        {flightType === "MULTICITY" && flights.length > 1 && (
          <hr className="my-6 border-0 border-b border-gray-200" />
        )}
      </React.Fragment>
    ));
  };

  return (
    <>
      <div className="w-full relative z-40" id="flightSearchForm">
        <div className="container">
          <form
            onSubmit={(e) => handleSubmit(e)}
            className={`p-8 bg-white rounded-2xl shadow-lg z-50 md:p-6 ${noOverlap ? "mt-0" : "-mt-40"}`}
          >
            <header className="flex justify-start items-center gap-6">
              <button
                type="button"
                className={`${
                  flightType === FlightTypeEnum.ONEWAY
                    ? "text-gray-800 border-b border-gray-800"
                    : "text-gray-600 border-b border-transparent"
                } capitalize pb-2 transition-all duration-300 hover:text-gray-800 focus:outline-none`}
                onClick={() => handleFlightTypeChange(FlightTypeEnum.ONEWAY)}
              >
                {t("oneway")}
              </button>
              <button
                type="button"
                className={`${
                  flightType === FlightTypeEnum.ROUNDWAY
                    ? "text-gray-800 border-b border-gray-800"
                    : "text-gray-600 border-b border-transparent"
                } capitalize pb-2 transition-all duration-300 hover:text-gray-800 focus:outline-none`}
                onClick={() => handleFlightTypeChange(FlightTypeEnum.ROUNDWAY)}
              >
                {t("roundway")}
              </button>
              <button
                type="button"
                className={`${
                  flightType === FlightTypeEnum.MULTICITY
                    ? "text-gray-800 border-b border-gray-800"
                    : "text-gray-600 border-b border-transparent"
                } capitalize pb-2 transition-all duration-300 hover:text-gray-800 focus:outline-none`}
                onClick={() => handleFlightTypeChange(FlightTypeEnum.MULTICITY)}
              >
                {t("multicity")}
              </button>
            </header>

            <div className="mt-6 mb-6">{renderFormFields()}</div>

            <div className="flex justify-end items-center gap-4 max-sm:flex-col">
              {flightType === "MULTICITY" && (
                <button
                  className="bg-gray-100 border-none flex items-center justify-center gap-2 text-gray-800 font-semibold rounded-full px-7 py-3.5 text-base leading-6 uppercase whitespace-nowrap transition-all duration-200 hover:scale-105 max-sm:w-full max-sm:hover:scale-100"
                  type="button"
                  onClick={handleAddMulticityFlight}
                >
                  {t("add_flight")}
                </button>
              )}

              {quoteData && quoteData.flights.length > 0 && (
                <Button
                  className="bg-red-500 border-none flex items-center justify-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 text-base leading-6 uppercase font-semibold whitespace-nowrap transition-all duration-200 hover:scale-105 max-sm:w-full max-sm:hover:scale-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  type="button"
                  onClick={resetForm}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                  style={{ backgroundColor: "red" }}
                >
                  {locale === "en" ? "Reset" : "إعادة تعيين"}
                </Button>
              )}

              <Button
                className="bg-[#5a558e] border-none flex items-center justify-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 text-base leading-6 uppercase font-semibold whitespace-nowrap transition-all duration-200 hover:scale-105 max-sm:w-full max-sm:hover:scale-100 disabled:opacity-50 disabled:cursor-not-allowed"
                type="submit"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                style={{ backgroundColor: "#5a558e" }}
              >
                {isSubmitting && (
                  <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                )}
                {isSubmitting
                  ? locale === "en"
                    ? "Please wait..."
                    : "يرجى الإنتظار..."
                  : t("getButton")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default FlightSearchForm;
